import { useState } from 'react'
import { KeyRound } from 'lucide-react'
import { Button, Form, TextField, useToast } from '@/components/play'
import type { SignInMethod, UserResource } from '@/types/users'
import { api } from '@/util/api'
import { failureStatus, failureType, fieldErrorsFrom } from './accountErrors'

const PASSWORD_FIELDS = ['current_password', 'new_password', 'confirm_password'] as const
const METHOD_NAMES: Partial<Record<SignInMethod, string>> = { google: 'Google', strava: 'Strava', email_link: 'an email link' }

/** The ways besides a password, as alternatives: "Google, Strava or an email link". */
function otherMethodList(methods: SignInMethod[]) {
  const names = methods.map((method) => METHOD_NAMES[method]).filter((name): name is string => Boolean(name))
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0]
}

/** Change password for accounts that have one. Accounts without one see how they sign in. */
export function PasswordSection({ user }: { user: UserResource }) {
  const methods = user.attributes.sign_in_methods
  // An API that doesn't report sign-in methods yet predates passwordless accounts: assume a password.
  const hasPassword = methods ? methods.includes('password') : true
  const otherMethods = otherMethodList(methods ?? [])

  if (!hasPassword) {
    return (
      <section className="ps-section" aria-labelledby="sign-in-heading">
        <h2 id="sign-in-heading">Sign-in</h2>
        <div className="ps-fields">
          <p className="pa-text">You sign in with {otherMethods ?? 'a connected account'}.</p>
        </div>
      </section>
    )
  }
  return <ChangePasswordForm alsoSignsInWith={otherMethods} />
}

function ChangePasswordForm({ alsoSignsInWith }: { alsoSignsInWith?: string }) {
  const { toast } = useToast()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)
  // A changed password starts a fresh form, so the emptied fields don't re-validate as missing.
  const [formKey, setFormKey] = useState(0)

  async function changePassword() {
    setSaving(true)
    setErrors({})
    try {
      await api.changePassword(currentPassword, newPassword)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setFormKey((key) => key + 1)
      toast({ title: 'Password changed', tone: 'success' })
    } catch (failure) {
      if (failureStatus(failure) === 401 && failureType(failure) === 'invalid_credentials') {
        setErrors({ current_password: ['Current password is incorrect'] })
        return
      }
      const fieldErrors = fieldErrorsFrom(failure, PASSWORD_FIELDS, { password: 'new_password' })
      if (fieldErrors) setErrors(fieldErrors)
      else toast({ title: 'Password not changed', description: 'Check your connection and try again.', tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="ps-section" aria-labelledby="password-heading">
      <h2 id="password-heading">Password</h2>
      <Form key={formKey} className="ps-fields" errors={errors} onFormSubmit={changePassword}>
        {alsoSignsInWith && <p className="pa-text">You can also sign in with {alsoSignsInWith}.</p>}
        <TextField
          name="current_password"
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          requiredMessage="Enter your current password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
        <TextField
          name="new_password"
          label="New password"
          type="password"
          autoComplete="new-password"
          description="At least 8 characters."
          required
          requiredMessage="Enter a new password"
          minLength={8}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          validate={(value) => (String(value ?? '').length < 8 ? 'Use at least 8 characters' : null)}
        />
        <TextField
          name="confirm_password"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          requiredMessage="Enter the new password again"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          validate={(value) => (value === newPassword ? null : 'The passwords don’t match')}
        />
        <div>
          <Button type="submit" loading={saving}>
            <KeyRound aria-hidden="true" />
            Change password
          </Button>
        </div>
      </Form>
    </section>
  )
}
